import { getAgentProfile } from "../api/agentProfile";
import { getBrokerProfile } from "../api/brokerProfile";
import { postLoginPath } from "../utils/siteLinks";

const ONBOARDING_KEY = "dh_onboarding";

export function isProfessionalRole(role) {
  return role === "agent" || role === "broker" || role === "corporate";
}

function readAll() {
  try {
    return JSON.parse(localStorage.getItem(ONBOARDING_KEY) || "{}");
  } catch {
    return {};
  }
}

function writeAll(map) {
  localStorage.setItem(ONBOARDING_KEY, JSON.stringify(map));
}

/** @returns {{ emailVerified: boolean, profileComplete: boolean, paidActivated: boolean, createdAt?: string }} */
export function getOnboardingRecord(userId) {
  if (!userId) {
    return { emailVerified: true, profileComplete: true, paidActivated: true };
  }
  const stored = readAll()[userId];
  if (!stored) {
    return { emailVerified: true, profileComplete: true, paidActivated: true };
  }
  return {
    emailVerified: Boolean(stored.emailVerified),
    profileComplete: Boolean(stored.profileComplete),
    paidActivated: Boolean(stored.paidActivated),
    createdAt: stored.createdAt || null,
  };
}

export function saveOnboardingRecord(userId, patch) {
  if (!userId) return getOnboardingRecord(userId);
  const all = readAll();
  const current = all[userId] || {};
  all[userId] = { ...current, ...patch, updatedAt: new Date().toISOString() };
  writeAll(all);
  return getOnboardingRecord(userId);
}

export function initOnboarding(userId, { role }) {
  const professional = isProfessionalRole(role);
  return saveOnboardingRecord(userId, {
    emailVerified: false,
    profileComplete: !professional,
    paidActivated: !professional,
    createdAt: new Date().toISOString(),
  });
}

export function markEmailVerified(userId) {
  return saveOnboardingRecord(userId, { emailVerified: true });
}

export function markProfileComplete(userId) {
  return saveOnboardingRecord(userId, { profileComplete: true });
}

export function markPaidActivated(userId) {
  return saveOnboardingRecord(userId, { paidActivated: true });
}

export function profileLooksComplete(session) {
  if (!session?.userId) return false;
  if (session.role === "broker" || session.role === "corporate") {
    const profile = getBrokerProfile(session.userId, session);
    return Boolean(
      String(profile.broker_name || session.name || "").trim() &&
        String(profile.license || "").trim() &&
        String(profile.office_name || session.office_name || "").trim()
    );
  }
  if (session.role === "agent") {
    const profile = getAgentProfile(session.userId, session);
    return Boolean(
      String(profile.agent_name || session.name || "").trim() &&
        String(profile.dre_license || "").trim() &&
        String(profile.office_name || session.office_name || "").trim()
    );
  }
  return true;
}

export function needsPasswordRotation(session) {
  return Boolean(session?.must_rotate);
}

export function needsEmailVerification(session) {
  if (!session?.userId || session.role === "admin") return false;
  if (session.betaTester) return false;
  if (session.emailVerified === true) return false;
  return !getOnboardingRecord(session.userId).emailVerified;
}

export function needsPaidActivation(session) {
  if (!session?.userId || session.role === "admin") return false;
  if (session.betaTester || session.plan === "beta") return false;
  if (!isProfessionalRole(session.role)) return false;
  if (session.plan_status === "active") return false;
  return !getOnboardingRecord(session.userId).paidActivated;
}

export function needsProfessionalSetup(session) {
  if (!session?.userId || !isProfessionalRole(session.role)) return false;
  if (session.betaTester && profileLooksComplete(session)) return false;
  const record = getOnboardingRecord(session.userId);
  if (record.profileComplete && profileLooksComplete(session)) return false;
  return !profileLooksComplete(session);
}

const ONBOARDING_PREFIXES = [
  "/verify-email",
  "/onboarding",
  "/forgot-password",
  "/reset-password",
];

export function isOnboardingPath(pathname = "") {
  return ONBOARDING_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/**
 * Ordered post-auth destination: security → verify → pay → profile → desk.
 */
export function resolvePostAuthPath(session, intendedRole) {
  if (!session) return "/login";
  if (needsPasswordRotation(session)) return "/onboarding/rotate-password";
  if (needsEmailVerification(session)) return "/verify-email/pending";
  if (needsPaidActivation(session)) return "/onboarding/subscribe";
  if (needsProfessionalSetup(session)) return "/onboarding/professional";
  return postLoginPath(session, intendedRole);
}

export function syncSessionOnboarding(session) {
  if (!session?.userId) return session;
  const record = getOnboardingRecord(session.userId);
  return {
    ...session,
    emailVerified: record.emailVerified,
    onboardingComplete: record.profileComplete && record.paidActivated && record.emailVerified,
  };
}
